import * as SentryMobile from '@sentry/react-native';
import * as SentryWeb from '@sentry/react';
import type { AxiosError, AxiosResponse } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { isMobileNative } from 'app/utils/web-helpers';
import type { RequestInterceptor } from '../types';

const SLOW_REQUEST_MS = 2000;
const MAX_BREADCRUMBS_PER_SESSION = 50;
let emittedBreadcrumbs = 0;
const recordedResponses = new WeakSet<AxiosResponse>();
const recordedErrors = new WeakSet<AxiosError>();

const STATIC_ROUTE_SEGMENTS = new Set([
  'activity',
  'accept',
  'address',
  'addresses',
  'all',
  'all-places',
  'ambassador',
  'analyze',
  'archive',
  'authentication',
  'availability',
  'badge',
  'batch-archive',
  'batch-delete',
  'batch-hide',
  'batch-unhide',
  'batch-unread',
  'bff',
  'bonification',
  'bonifications',
  'bulk-sessions',
  'by_category',
  'cancel',
  'categories',
  'channels',
  'change-password',
  'chat',
  'closest_v2',
  'comments',
  'complete',
  'conversations',
  'createimagevariantsfromurl',
  'daily-stats',
  'dates',
  'discover',
  'dismissals',
  'distance',
  'drafts',
  'explore_aggregate_counts',
  'favourites',
  'find',
  'forgot-password',
  'generate',
  'geocode',
  'grouped',
  'groupings',
  'groups',
  'guidelines',
  'interactions',
  'interests',
  'invites',
  'join',
  'leave',
  'lenders',
  'like',
  'link-offer',
  'listing',
  'listings',
  'loan',
  'loan-fee',
  'loanfee',
  'loans',
  'many',
  'mark-as-read',
  'mark-need-match',
  'members',
  'messages',
  'metrics',
  'metas',
  'monthly-stats',
  'multiple',
  'my-places',
  'neighbourhood-status',
  'notifications',
  'order',
  'participations',
  'payment-sheet',
  'pending',
  'photos',
  'picture',
  'pictures',
  'plans',
  'popular_items',
  'possible_lenders',
  'posts',
  'predictions',
  'preferences',
  'profiles',
  'rating',
  'ratings',
  'reactions',
  'recent',
  'recommender',
  'referred-by',
  'regenerate',
  'register',
  'reject',
  'report',
  'reset',
  'retry',
  'role',
  'save',
  'services',
  'sharing-places',
  'silence',
  'similarities',
  'sso',
  'statistics',
  'stop',
  'tags',
  'test',
  'top',
  'transactions',
  'types',
  'unavailability',
  'unread',
  'unregister',
  'user',
  'user_profile',
  'useritem',
  'users',
  'verify',
]);

export function normalizeRouteForTelemetry(url?: string): string {
  const path = (url ?? '/').split('?')[0] ?? '/';
  const segments = path.split('/');

  return segments
    .map((segment, index) => {
      if (!segment) return segment;
      let decodedSegment: string;
      try {
        decodedSegment = decodeURIComponent(segment);
      } catch {
        return ':id';
      }
      if (STATIC_ROUTE_SEGMENTS.has(decodedSegment.toLowerCase())) return decodedSegment;
      if (segments[index - 1]?.toLowerCase() === 'authentication') return ':credential';
      return ':id';
    })
    .join('/');
}

function serverDuration(response?: AxiosResponse): number | undefined {
  const value = response?.headers['server-timing'];
  if (typeof value !== 'string') return undefined;
  const match = /(?:^|,)\s*app;dur=([\d.]+)/i.exec(value);
  return match ? Number(match[1]) : undefined;
}

function addPerformanceBreadcrumb(response: AxiosResponse | undefined, error?: AxiosError): void {
  const config = response?.config ?? error?.config;
  const timing = config?.lpcPerformance;
  if (!timing || emittedBreadcrumbs >= MAX_BREADCRUMBS_PER_SESSION) return;

  const totalMs = Date.now() - timing.startedAt;
  if (!error && totalMs < SLOW_REQUEST_MS) return;
  emittedBreadcrumbs += 1;

  const breadcrumb = {
    category: 'http.performance',
    data: {
      method: config.method?.toUpperCase() ?? 'GET',
      requestId: timing.requestId,
      route: normalizeRouteForTelemetry(config.url),
      serverMs: serverDuration(response),
      status: response?.status ?? error?.response?.status,
      totalMs,
    },
    level: error ? ('error' as const) : ('info' as const),
    message: error ? 'HTTP request failed' : 'Slow HTTP request',
  };

  if (isMobileNative) SentryMobile.addBreadcrumb(breadcrumb);
  else SentryWeb.addBreadcrumb(breadcrumb);
}

export const requestPerformance: RequestInterceptor = (config) => {
  const requestId = uuidv4();
  config.lpcPerformance = { requestId, startedAt: Date.now() };
  config.headers['X-Request-Id'] = requestId;
  return config;
};

export function responsePerformance(response: AxiosResponse): AxiosResponse {
  if (!recordedResponses.has(response)) {
    recordedResponses.add(response);
    addPerformanceBreadcrumb(response);
  }
  return response;
}

export function responsePerformanceError(error: AxiosError): Promise<never> {
  if (!recordedErrors.has(error)) {
    recordedErrors.add(error);
    addPerformanceBreadcrumb(error.response, error);
  }
  return Promise.reject(error);
}
